import React, { useCallback, useEffect } from "react";
import { useState } from "react";

import DataFrame from "dataframe-js";
import { Tab, Tabs } from "react-bootstrap";

import Widget from "../../../components/Elements/Widget";
import { AssetLink, SoilProfile } from "../../../types/types";
import StackedPlot from "../../../components/Elements/StackedPlot";
import {
  colorPlot,
  conductivityPlot,
  structurePlot,
  texturePlot,
  waterPlot,
  healthPlot,
} from "../utils/charts";
import { useGetProfileCsv } from "../../../api/useGetProfileCsv";
import AssetModal from "../../../components/Elements/AssetModal";
import ProfileVideoButton from "./ProfileVideoButton";

type Props = {
  soilProfile: SoilProfile | null;
};

const BelowWidget = ({ soilProfile }: Props) => {
  console.log("soilProfile", soilProfile);
  const [data, setData] = useState<any>(null);
  const { data: csvData, isLoading } = useGetProfileCsv(soilProfile);
  const [selectedAssetLink, setSelectedAssetLink] = useState<AssetLink | null>(null);
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [maxDepthCmArray, setMaxDepthCmArray] = useState<Array<Array<number>>>([[0,0]]);
  const [maxVideoTime, setMaxVideoTime] = useState<number>(0);
  const [minVideoTime, setMinVideoTime] = useState<number>(0);
  const [depthIndicatorPosition, setDepthIndicatorPosition] = useState<number>(0);
  const [depthIndicatorCm, setDepthIndicatorCm] = useState<number>(0);

  useEffect(() => {
    console.log("isLoading", isLoading)
    console.log("csvData", csvData)
    if (!isLoading && csvData) {
      var file = new File([csvData], "temp.csv", {
        type: "text/plain",
      });
      console.log("file", file)
      DataFrame.fromCSV(file).then((df: any) => {
        //console.log("df", df)
        const columns = df.listColumns();
        for(let col = 0; col < columns.length; col++) {
          df = df.rename(columns[col], columns[col].toLocaleLowerCase());
        }
        df = df.filter((row:any) => {
          //console.log("row.get('video time (s)'", row.get('video time (s)'));
          if (row.get('video time (s)') === ''){
            // Do nothing for now.
          }else{
            return row.get('video time (s)');// >= 0
          }
        }); 
        //console.log("df", df);
        let availableColumns = df.listColumns();
        let porosPointArray = [];
        if (availableColumns.includes('pores')){
          porosPointArray = df.toArray('poros');
          const allValuesBetween0And1Poros = porosPointArray.every((value: number) => value >= 0 && value <= 1);
          //modifiedPorosPointArray = allValuesBetween0And1Poros ? porosPointArray : porosPointArray.map((value: number) => value / 100);
          //df = df.withColumn('poros_adj', (row: any) => modifiedPorosPointArray.shift());
          if (!allValuesBetween0And1Poros){
            //console.log("E103024 ------ porosPointArray", porosPointArray);
            df = df.map((row:any) => row.set('poros', row.get('poros') / 100));
          }
        }
        setData(df);
        let maxCm = Math.ceil(Number(df.stat.max("depth (cm)")) / 10) * 10
        let cmIntervals = [];
        let i = 0;
        while (i <= maxCm) {
          if (i % 10 === 0){
            cmIntervals.push([i,(i/maxCm) * 100])
          } 
          i++;
        }
        setMinVideoTime(Number(df.stat.min("video time (s)")));
        setMaxVideoTime(Number(df.stat.max("video time (s)")));
        setMaxDepthCmArray(cmIntervals);
        setDepthIndicatorPosition(0);
        setDepthIndicatorCm(0);
        let timeDepthArray = df.select("video time (s)","depth (cm)").toArray();
        timeDepthArray.unshift(["video time (s) - 4x less than video time","depth (cm)"])
      });
    }
  }, [csvData]);

  useEffect(() => {
    setData(null);
  }, [soilProfile]);

  const onPlotClick = useCallback(
    (depth: number) => {
      depth = Math.round(depth);

      // plot allows selection outside of the range, clamp to the range of asset
      const depths = soilProfile?.assetLinks.map((l: AssetLink) => l.value);
      const maxDepth = Math.max(...depths!);
      depth = depth > maxDepth ? maxDepth : depth;
      depth = depth < 1 ? 1 : depth;

      const asset = soilProfile?.assetLinks.find(
        (link: AssetLink) => link.asset_type == 'depth_snapshot' && link.value === depth
      );
      if (asset) {
        setSelectedAssetLink(asset);
      }
    },
    [soilProfile, data]
  );

  const handleTimeUpdate = (e:any) => {
    console.log("Video currentTime / 4", e.target.currentTime / 4);

    const maxTime = Number(data.stat.max("video time (s)"))
    const maxDepth = Number(data.stat.max("depth (cm)"))

    let beforeDepthTime = 0;
    let beforeDepthCm = 0;
    const beforeDepthArray = data.filter((row:any ) =>  row.get("video time (s)") <= (e.target.currentTime / 4 ) );

    if (beforeDepthArray.toArray().length > 0){
      let indexA = beforeDepthArray.toArray("video time (s)").length - 1;
      beforeDepthTime = Number(beforeDepthArray.toArray("video time (s)")[indexA]);
      beforeDepthCm = Number(beforeDepthArray.toArray("depth (cm)")[indexA]);
    }
    
    let afterDepthTime = maxTime;
    let afterDepthCm = maxDepth;
    const afterDepthArray = data.filter((row:any ) =>  row.get("video time (s)") >= (e.target.currentTime / 4 ) );

    if (afterDepthArray.toArray().length > 0){
      let indexB = 0;
      afterDepthTime = Number(afterDepthArray.toArray("video time (s)")[indexB]);
      afterDepthCm = Number(afterDepthArray.toArray("depth (cm)")[indexB]);
    }

    if (maxDepthCmArray.length > 0){
      let addedInterpolation = 0;
      if (afterDepthTime > beforeDepthTime){
        let tare = (( e.target.currentTime / 4 ) - beforeDepthTime) / (afterDepthTime - beforeDepthTime);
        addedInterpolation = tare * (afterDepthCm - beforeDepthCm);
      }

      setDepthIndicatorPosition(( (beforeDepthCm + addedInterpolation) / maxDepthCmArray[maxDepthCmArray.length - 1][0] ) * 100 );
      setDepthIndicatorCm((beforeDepthCm + addedInterpolation));

    }
  }

  return (
    <>
      <Widget
        title={`Soil Push #${soilProfile?.name}`}
        renderActions={() => <></>}
      >
        {!isLoading && data && soilProfile 
        ? (
          <div className="w-100 p-1">
            {soilProfile.maskedVideoUrl && <ProfileVideoButton onClick={() => setShowVideo(true)} />}

            <Tabs
              unmountOnExit={true}
              defaultActiveKey="profile"
              transition={false}
              id="tabs"
              className="p-2 mb-3"
            >
              <Tab eventKey="profile" title="Profile" tabClassName="text-sm p-2">
                <div>
                  <StackedPlot
                    config={colorPlot(soilProfile, data)}
                    onClick={onPlotClick}
                  />
                </div>
              </Tab>
              <Tab eventKey="texture" title="Texture" tabClassName="text-sm p-2">
                <StackedPlot
                  config={texturePlot(soilProfile, data)}
                  onClick={onPlotClick}
                />
              </Tab>
              <Tab eventKey="structure" title="Structure" tabClassName="text-sm p-2">
                <StackedPlot
                  config={structurePlot(data)}
                  onClick={onPlotClick}
                />
              </Tab>
              <Tab eventKey="water" title="Water" tabClassName="text-sm p-2">
                <StackedPlot
                  config={waterPlot(soilProfile, data)}
                  onClick={onPlotClick}
                />
              </Tab>
              <Tab eventKey="conductivity" title="Conductivity" tabClassName="text-sm p-2">
                <StackedPlot
                  config={conductivityPlot(data)}
                  onClick={onPlotClick}
                />
              </Tab>
              <Tab eventKey="carbon" title="Carbon" tabClassName="text-sm p-2">
                <StackedPlot
                  config={healthPlot(soilProfile, data)}
                  onClick={onPlotClick}
                />
              </Tab>
            </Tabs>
          </div>
        ) 
        : <div className="w-100 p-1"></div>
      }
      </Widget>

      {soilProfile && selectedAssetLink && (
        <AssetModal
          show={true}
          title={`Push #${soilProfile.name}: Depth ${selectedAssetLink.value}cm`}
          onClose={() => setSelectedAssetLink(null)}
        >
          <img src={selectedAssetLink.s3_key} style={{ width: "100%" }} />
        </AssetModal>
      )}

      {soilProfile && soilProfile.maskedVideoUrl && showVideo && (
        <AssetModal
          show={showVideo}
          title={`Push #${soilProfile.name}`}
          onClose={() => setShowVideo(false)}
        >
          <div style={{width:"100%", height: "100%", display: "inline-flex"}}>
            <div style={{width:"95px", paddingBottom: "40px", fontSize: "12px", minWidth: "95px"}} >
            <div style={{fontWeight:"bold", position:"relative", top:"-10px", borderBottom:"0.5px solid #fff", marginRight:"20px"}} >depth (cm)</div>
              { maxDepthCmArray.map((item, index) => (
                <div 
                  key={`${index}-depth`}
                  className={"div-interval-label"}
                  style={{top:`${item[1]}%`}}
                >
                  {item[0]}
                  <div 
                    className={"div-interval-label-tic"}
                  ></div>
                </div>
              ))
              }
              <div 
                key={`current-depth`}
                className={"div-interval-label"}
                style={{top:`${depthIndicatorPosition}%`, left:"45px", color:"#6d757d"}}
              >
                  {`${depthIndicatorCm.toFixed(1)} cm`}
                <div 
                  className={"div-interval-label-tic"}
                  style={{backgroundColor:"#6d757d", width:"20px", left:"-22px"}}
                ></div>
              </div>
            </div>
            
            <div style={{width:"calc(100% - 95px)"}} >
              <div
                  style={{
                    width: "calc(100% / 23)",
                    height: "2px",
                    backgroundColor: "white",
                    position: "relative",
                    top: "50px",
                    left: "30px",
                  }} 
              >
                <div
                    style={{
                      width: "200%",
                      height: "20px",
                      position: "relative",
                      top: "-15px",
                      left: "-50%",
                      fontSize:"8px",
                      textAlign:"center",
                    }} 
                >100 microns</div>
              </div>
              <video
                onTimeUpdate={handleTimeUpdate}
                src={`${soilProfile.maskedVideoUrl}#t=${ minVideoTime * 4 }`}
                controls
                className="w-100 mt-2"
                id="soilProbeVideo"
              >
                Your browser does not support this video
              </video>
            </div>
          </div>
        </AssetModal>
      )}
    </>
  );
};

export default BelowWidget;
